/**
 * LO QUE EL MOTOR DEL CUADERNO RECIBE (CQ2): las filas de Pest, ya leídas, sin
 * red. Quien las carga (CQ4: el asistente en el servidor y el botón de
 * `/spray-logs`) las trae de Supabase y se las pasa tal cual a
 * `construirCuaderno` (`motor.ts`).
 *
 * Interfaces de TypeScript y NO un esquema zod a propósito: con
 * `strictNullChecks` apagado, zod infiere TODO opcional y el compilador dejaría
 * pasar una entrada sin partes. Aquí lo obligatorio es obligatorio.
 *
 * Los campos de la ola 2 (máquina, aplicador, tiempo) y la eficacia (llega con
 * F123) son OPCIONALES: si faltan, el motor deja el hueco y lo pregunta; nunca
 * los inventa.
 */
import type { RegistryItem } from "@/lib/registrySuggest";
import type { DatosLegalesRow } from "./datosLegales";
import type { EdicionLinea } from "./borrador";

/** Fecha ISO «aaaa-mm-dd». */
export type Iso = string;

export interface FincaEntrada {
  id: string;
  nombre: string;
}

export interface NaveEntrada {
  id: string;
  nombre: string;
  locationId: string | null;
}

/** Un sector (`blocks`). */
export interface SectorEntrada {
  id: string;
  naveId: string;
  nombre?: string | null;
  areaM2?: number | null;
  /** Cultivo del sector cuando no hay ciclo (`blocks.crop_name` / `variety_name`). */
  cultivo?: string | null;
  /** Código SIEX del espejo del sector (`blocks.crop_code`, F4 cultivos SIEX). Solo afina el nombre impreso. */
  cultivoCodigo?: number | null;
  variedad?: string | null;
}

/** Un ciclo de cultivo (`crop_cycles`). */
export interface CicloEntrada {
  id: string;
  blockId: string | null;
  especie: string;
  /**
   * Código SIEX del ciclo (`crop_cycles.crop_code`, F4 cultivos SIEX). Con él la
   * especie impresa sale del código (`especieOficial(nombre, código)`); la clave
   * de la parcela NO cambia (sigue siendo nave × especie × variedad × ciclo).
   */
  especieCodigo?: number | null;
  variedad?: string | null;
  inicio: Iso;
  fin?: Iso | null;
}

/** Un recinto SIGPAC guardado (`greenhouse_sigpac_recintos`). Solo cuentan los confirmados. */
export interface RecintoEntrada {
  greenhouseId: string;
  provincia: string;
  municipioCodigo: string;
  municipioNombre?: string | null;
  agregado: string;
  zona: string;
  poligono: string;
  parcela: string;
  recinto: string;
  usoSigpac?: string | null;
  superficieHa?: number | null;
  confirmadoEn?: string | null;
  /** Fracción (0–1) de la nave dentro del recinto, medida al confirmarlo (25-10-2026). */
  parteNave?: number | null;
  /** Puntos de la nave que el WMS dio en este recinto (la cuenta aproximada). */
  muestras?: number | null;
}

/** Aplicador del parte (F3). `tipo` distingue persona y empresa de servicios. */
export interface AplicadorEntrada {
  id: string;
  nombre: string;
  /**
   * NO sale de `spray_logs.applicator_snapshot`, que no lo lleva (E11: el parte
   * lo lee toda la empresa). Quien carga (CQ4) lo trae de `company_applicators`
   * por `applicator_id`, que solo leen admin y encargado.
   */
  nif?: string | null;
  ropo?: string | null;
  carne?: "basico" | "cualificado" | "fumigador" | "piloto" | null;
}

/** Máquina del parte (F2). `roma`: nº ROMA, o de censo si no va al ROMA. */
export interface EquipoEntrada {
  id: string;
  descripcion: string;
  roma?: string | null;
  fechaAdquisicion?: Iso | null;
  fechaUltimaInspeccion?: Iso | null;
}

/** Tiempo exterior de un parte (F1). La lista la comparten el cargador y los cambios del asistente. */
export const CONDICIONES_TIEMPO = ["soleado", "nublado", "lluvia"] as const;
export type CondicionTiempo = (typeof CONDICIONES_TIEMPO)[number];
/** Eficacia de un parte (F123, D17). La lista la comparten el cargador y los cambios del asistente. */
export const EFICACIAS = ["buena", "regular", "mala"] as const;
export type Eficacia = (typeof EFICACIAS)[number];

/** Un parte (`spray_logs`): un sector, un día. */
export interface ParteEntrada {
  id: string;
  fecha: Iso;
  naveId: string | null;
  blockId: string | null;
  cropCycleId?: string | null;
  /** `block_area_m2_snapshot`: el área tratada ese día. Sin él se mira el sector. */
  areaM2?: number | null;
  notas?: string | null;
  taskId?: string | null;
  // ── Ola 2 (opcionales) ──
  equipo?: EquipoEntrada | null;
  aplicador?: AplicadorEntrada | null;
  tiempo?: { condicion: CondicionTiempo; fuente: "medido" | "default" | "manual" } | null;
  /** Método de aplicación y si fue general o localizada («Pulverización localizada en focos»). */
  metodo?: string | null;
  /**
   * CU2 (27-09-2026): máquina y/o aplicador que el parte NO guardó (partes de antes
   * del 26-09) y el cargador DEDUCE con la regla del servidor (`pickEquipment`,
   * `pickApplicator`). Se imprimen con origen «deducido» («confírmalo»), nunca como
   * dato guardado. Sin él, lo que trae el parte es lo guardado.
   */
  deducido?: { aplicador?: boolean; equipo?: boolean } | null;
  // ── F123 ──
  eficacia?: Eficacia | null;
  eficaciaFuente?: "defecto" | "usuario" | null;
  /**
   * Cuándo se GUARDÓ el parte (`spray_logs.created_at`, ISO). La confirmación en
   * bloque de la eficacia (D17) solo vale para los partes guardados antes de ella
   * (M1 de la revisión de la ola 3). Sin él, no se da por confirmada.
   */
  creadoEn?: string | null;
}

/** Una línea del parte (`spray_log_entries`): un producto. */
export interface LineaEntrada {
  id: string;
  parteId: string;
  /** `product_id ?? inventory_item_id`: hay DOS FK a inventory. */
  productoId?: string | null;
  nombre: string;
  categoria?: string | null;
  /** `registration_number_snapshot`. */
  registro?: string | null;
  lote?: string | null;
  plaga?: string | null;
  /** Cantidad ABSOLUTA aplicada en ese parte (`amount_used`), en `unidad`. */
  cantidad?: number | null;
  unidad?: string | null;
  /** La dosis tal y como se escribió (`applied_dose_value/unit`): respaldo si no hay cantidad. */
  dosisValor?: number | null;
  dosisUnidad?: string | null;
  /** Caldo (`water_volume_l` + `water_volume_unit`: «L» total o «L/ha»). */
  agua?: number | null;
  aguaUnidad?: string | null;
  /**
   * CU9: la línea NO viene de ningún parte: la añadió la persona al documento
   * (`add_line`). La monta `buildCuaderno` con el id de la línea añadida en
   * `claveEdicion` (una por nave). Nunca la escribe nadie en `spray_log_entries`.
   */
  anadida?: boolean;
  /** CU9: la clave de sus ediciones en el borrador si no es su `id` (las líneas añadidas). */
  claveEdicion?: string | null;
}

/** Lo que hoy tiene el producto en el inventario (respaldo del nº y unidad de stock). */
export interface ProductoInventario {
  id: string;
  registro?: string | null;
  unidad?: string | null;
  categoria?: string | null;
}

/** Decisiones del documento (el borrador de CQ4, D16): nunca hechos de la aplicación. */
export interface DecisionesCuaderno {
  /** Líneas que NO van al cuaderno, con su motivo. Desde CU9 (28-09-2026) sale cualquiera, también un fitosanitario: sale del DOCUMENTO, el parte sigue. */
  excluidas?: Record<string, string>;
  /** SI/NO del 3.2 al 3.5 contestados. Sin contestar: NO por defecto (D15), a confirmar. */
  aplica?: Partial<Record<"3.2" | "3.3" | "3.4" | "3.5", boolean>>;
  /** El periodo ya lo ha confirmado alguien. */
  periodoConfirmado?: boolean;
  /** ¿Hubo análisis de residuos? false = contestado que no. */
  huboAnalisis?: boolean | null;
  /**
   * D17 + CU2 (27-09-2026): la eficacia «Buena» confirmada en bloque en el borrador
   * (`eficacia_confirmada` = sí), con su hora (`en`, M1). Con ella el motor imprime
   * «Buena» en los partes SIN eficacia guardados antes de esa hora (no toca los que
   * la tienen); sin ella, la eficacia que falta es un hueco que se pregunta.
   */
  eficaciaConfirmadaHasta?: string | null;
  /**
   * CU9 (28-09-2026, «el documento es de la persona»): lo que la persona quiere que
   * diga el documento de cada línea, por su clave (id de la línea o de la añadida).
   * El motor lo pone ENCIMA de lo que sale de los partes, con origen «usuario».
   */
  ediciones?: Record<string, EdicionLinea>;
}

export interface EntradaCuaderno {
  /** Año natural del cuaderno (D14). */
  anio: number;
  /** Opción de campaña («2025/26»): si viene, manda sobre el año natural. */
  campana?: { desde: Iso; hasta: Iso; etiqueta: string } | null;
  /** Datos legales del TITULAR de este cuaderno (fila de `farm_legal_profiles`). */
  legal?: DatosLegalesRow | null;
  /**
   * Fincas que entran en ESTE cuaderno (D14: por titular). null = todas las de
   * `fincas`. Las que tienen otro titular van en otro cuaderno.
   */
  fincasDelTitular?: string[] | null;
  fincas: FincaEntrada[];
  naves: NaveEntrada[];
  sectores: SectorEntrada[];
  ciclos: CicloEntrada[];
  recintos?: RecintoEntrada[];
  partes: ParteEntrada[];
  lineas: LineaEntrada[];
  inventario?: ProductoInventario[];
  /** Catálogo del MAPA (`phyto-registry-catalog`). Sin él solo se mira el formato del nº. */
  catalogo?: RegistryItem[] | null;
  /** D26: false para registros que no son del MAPA (Chinampa). */
  mapaValidation?: boolean;
  /** Máquinas y aplicadores dados de alta en la empresa (para proponer cuando falta en un parte). */
  equiposEmpresa?: EquipoEntrada[];
  aplicadoresEmpresa?: AplicadorEntrada[];
  decisiones?: DecisionesCuaderno;
}
